import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getAssignedCustomer, getSaleGenderPrice, hasAssignedCustomer } from '@/components/pos/posCustomer'
import {
  resolvePosPricingGender,
  shouldUpdateSaleGenderPrice,
} from '@/components/pos/posCatalog'
import { getParentSaleLines, getParentSaleLinesForDisplay, toSaleHeaderMeta, toPosTotals } from '@/components/pos/posCart'
import { canOpenPaymentModal, canPaySaleLines } from '@/components/pos/posPayment'
import { canHoldSaleLines } from '@/components/pos/posHold'
import { printThermalReceipt } from '@/components/pos/posReceipt'
import {
  getRememberedLastCompletedSale,
  rememberLastCompletedSale,
} from '@/components/pos/posLastReceipt'
import { printServiceSlips } from '@/components/pos/posServiceSlip'
import { focusPosCustomerSearch } from '@/components/pos/posShortcuts'
import { printTaxInvoice } from '@/components/pos/posTaxInvoiceUtils'
import { usePosKeyboard } from '@/components/pos/usePosKeyboard'
import { PosServiceSlips } from '@/components/pos/PosServiceSlips'
import { PosAppointmentModal } from '@/components/pos/PosAppointmentModal'
import { PosPackageWizardModal } from '@/components/pos/PosPackageWizardModal'
import { PosPackageRedeemModal } from '@/components/pos/PosPackageRedeemModal'
import { PosSuspendedModal } from '@/components/pos/PosSuspendedModal'
import { PosPaymentModal } from '@/components/pos/PosPaymentModal'
import { PosTaxInvoice } from '@/components/pos/PosTaxInvoice'
import { PosThermalReceipt } from '@/components/pos/PosThermalReceipt'
import { PosDiscountModal } from '@/components/pos/PosDiscountModal'
import { PosTicketNoteModal } from '@/components/pos/PosTicketNoteModal'
import { PosCustomerHistoryModal } from '@/components/pos/PosCustomerHistoryModal'
import { PosLineStylistModal } from '@/components/pos/PosLineStylistModal'
import { PosLinePriceModal } from '@/components/pos/PosLinePriceModal'
import { PosLineAddonModal } from '@/components/pos/PosLineAddonModal'
import { PosNewGuestModal } from '@/components/pos/PosNewGuestModal'
import { adjustLineQuantity } from '@/components/pos/posShortcuts'
import { rememberRecentService } from '@/components/pos/usePosRecentServices'
import { PosLayout } from '@/components/pos/PosLayout'
import { PosOfflineBanner } from '@/components/pos/PosOfflineBanner'
import { DayOpenGate } from '@/components/pos/DayOpenGate'
import { POS_CUSTOMER } from '@/components/pos/posMockData'
import { usePosBarcodeScanner } from '@/components/pos/usePosBarcodeScanner'
import { usePosCatalog } from '@/components/pos/usePosCatalog'
import { usePosProducts } from '@/components/pos/usePosProducts'
import { usePosQuickKeys } from '@/components/pos/usePosQuickKeys'
import { usePosSale } from '@/components/pos/usePosSale'
import { useBranch } from '@/contexts/BranchContext'
import { defaultPhoneCountryForBranch } from '@/lib/phone'
import {
  fetchLastCompletedSale,
  fetchSaleReceipt,
  fetchSaleServiceSlips,
  fetchSaleTaxInvoice,
} from '@/lib/api/sales'
import { fetchOpenCashShift, openCashShift } from '@/lib/api/accounts'
import { fetchPaymentGatewaySettings, chargeSaleGateway } from '@/lib/api/paymentGateway'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { SALON_DESK_CHECKOUT } from '@/lib/salonDeskConfig'

export function PosPage() {
  const [catalogMode, setCatalogMode] = useState('service')
  const [dayGateLoading, setDayGateLoading] = useState(true)
  const [dayOpen, setDayOpen] = useState(false)
  const [dayGateBusy, setDayGateBusy] = useState(false)
  const [dayGateError, setDayGateError] = useState('')
  const [activeCategoryId, setActiveCategoryId] = useState(null)
  const [catalogGenderFilter, setCatalogGenderFilter] = useState(SALON_DESK_CHECKOUT.fixedPricingGender)
  const [paymentOpen, setPaymentOpen] = useState(false)
  const [gatewaySettings, setGatewaySettings] = useState({ enabled: false, test_mode: true })
  const [suspendedOpen, setSuspendedOpen] = useState(false)
  const [appointmentOpen, setAppointmentOpen] = useState(false)
  const [packageOpen, setPackageOpen] = useState(false)
  const [redeemOpen, setRedeemOpen] = useState(false)
  const [discountOpen, setDiscountOpen] = useState(false)
  const [discountTab, setDiscountTab] = useState('bill')
  const [noteOpen, setNoteOpen] = useState(false)
  const [stylistModalOpen, setStylistModalOpen] = useState(false)
  const [stylistFocusSplit, setStylistFocusSplit] = useState(false)
  const [priceModalOpen, setPriceModalOpen] = useState(false)
  const [addonModalOpen, setAddonModalOpen] = useState(false)
  const [customerHistoryOpen, setCustomerHistoryOpen] = useState(false)
  const [receipt, setReceipt] = useState(null)
  const [taxInvoice, setTaxInvoice] = useState(null)
  const [serviceSlips, setServiceSlips] = useState(null)
  const [selectedLineId, setSelectedLineId] = useState(null)
  const [shortcutNotice, setShortcutNotice] = useState('')
  const [lastCompletedSale, setLastCompletedSale] = useState(null)
  const [reprinting, setReprinting] = useState(false)
  const [reprintError, setReprintError] = useState('')
  const [requiredGuestOpen, setRequiredGuestOpen] = useState(false)
  const pendingGuestActionRef = useRef(/** @type {((sale: import('@/types/api').Sale | null) => void | Promise<void>) | null} */ (null))
  const receiptRootRef = useRef(null)
  const taxInvoiceRootRef = useRef(null)
  const serviceSlipRootRef = useRef(null)

  const {
    categories,
    services,
    loadingCategories,
    loadingServices,
    error: catalogError,
  } = usePosCatalog({
    catalogGenderFilter,
    activeCategoryId,
    onCategoryChange: setActiveCategoryId,
  })

  const {
    sale,
    stylists,
    paymentMethods,
    loadingPaymentMethods,
    addonOptionsByService,
    loading: saleLoading,
    mutating,
    error: saleError,
    customerError,
    paymentError,
    holdError,
    appointmentError,
    packageError,
    redeemError,
    suspendedSales,
    loadingSuspended,
    branchLabel,
    addService,
    addProduct,
    assignStylist,
    changeLineQuantity,
    attachAddon,
    detachAddon,
    voidLine,
    changeGenderPrice,
    assignCustomer,
    clearCustomer,
    paySale,
    startNewTicket,
    holdCurrentSale,
    recallSuspendedSale,
    loadSuspendedSales,
    loadAppointmentOntoTicket,
    sellPackage,
    redeemPackageService,
    redeemPackageServices,
    ensureAddonOptions,
    duplicateLine,
    updateLineDetails,
    updateSaleNotes,
    applyLineDiscountToSale,
    applyHeaderDiscountToSale,
    applyPromoCodeToSale,
    removePromoCodeFromSale,
    applyLoyaltyRedemptionToSale,
    removeLoyaltyRedemptionFromSale,
    discountError,
    loyaltyError,
    floorQueueError,
    enqueueToFloorQueue,
    advanceLineStatus,
  } = usePosSale({ initialGender: POS_CUSTOMER.gender })

  const { activeBranchId, activeBranch } = useBranch()
  const guestPhoneCountry = defaultPhoneCountryForBranch(activeBranch?.country)

  useEffect(() => {
    let cancelled = false
    async function checkDay() {
      setDayGateLoading(true)
      setDayGateError('')
      try {
        const shift = await fetchOpenCashShift()
        if (!cancelled) setDayOpen(Boolean(shift))
      } catch (err) {
        if (!cancelled) {
          setDayOpen(false)
          setDayGateError(apiErrorMessage(err, 'Could not check if the day is open.'))
        }
      } finally {
        if (!cancelled) setDayGateLoading(false)
      }
    }
    void checkDay()
    return () => {
      cancelled = true
    }
  }, [activeBranchId])

  async function handleDayOpenFromGate(openingFloat) {
    setDayGateBusy(true)
    setDayGateError('')
    try {
      await openCashShift(openingFloat)
      setDayOpen(true)
    } catch (err) {
      setDayGateError(apiErrorMessage(err, 'Could not open the day.'))
    } finally {
      setDayGateBusy(false)
    }
  }

  const {
    quickKeys,
    loading: loadingQuickKeys,
    error: quickKeysError,
  } = usePosQuickKeys({
    branchId: activeBranchId,
    enabled: !SALON_DESK_CHECKOUT.lite,
  })

  const {
    products,
    loading: loadingProducts,
    error: productsError,
  } = usePosProducts({
    branchId: activeBranchId,
    enabled: SALON_DESK_CHECKOUT.showRetailAndQuickKeys || !SALON_DESK_CHECKOUT.lite,
  })

  const pricingGender = useMemo(
    () => resolvePosPricingGender(catalogGenderFilter, sale?.gender_price),
    [catalogGenderFilter, sale?.gender_price],
  )

  const handleGenderChange = useCallback(async (nextFilter) => {
    if (SALON_DESK_CHECKOUT.lite) return
    setCatalogGenderFilter(nextFilter)
    if (shouldUpdateSaleGenderPrice(nextFilter)) {
      await changeGenderPrice(nextFilter)
    }
  }, [changeGenderPrice])

  const handleSelectCustomer = useCallback(async (customer) => {
    const updated = await assignCustomer(customer.id)
    const nextGender = getSaleGenderPrice(updated)
    if (nextGender) {
      setCatalogGenderFilter(nextGender)
    }
  }, [assignCustomer])

  const handleClearCustomer = useCallback(async () => {
    await clearCustomer()
  }, [clearCustomer])

  const handleSelectLine = useCallback((lineId) => {
    setSelectedLineId(lineId)
  }, [])

  const handleServiceSelect = useCallback(async (service) => {
    const updated = await addService(service)
    if (!updated) return

    if (activeBranchId) {
      rememberRecentService(activeBranchId, service)
    }

    const matchingLine = getParentSaleLines(updated).find(
      (line) =>
        line.line_type === 'service' &&
        line.service_id === service.id &&
        !line.customer_package_id,
    )
    const parentLines = getParentSaleLines(updated)
    setSelectedLineId(matchingLine?.id ?? parentLines[parentLines.length - 1]?.id ?? null)
  }, [activeBranchId, addService])

  const handleProductSelect = useCallback(async (product) => {
    setCatalogMode('retail')
    const updated = await addProduct(product)
    if (!updated) return

    const parentLines = getParentSaleLines(updated)
    setSelectedLineId(parentLines[parentLines.length - 1]?.id ?? null)
  }, [addProduct])

  const handleBarcodeScan = useCallback(async (product) => {
    setCatalogMode('retail')
    const updated = await addProduct(product)
    if (!updated) return

    const parentLines = getParentSaleLines(updated)
    setSelectedLineId(parentLines[parentLines.length - 1]?.id ?? null)
  }, [addProduct])

  usePosBarcodeScanner({
    enabled: !SALON_DESK_CHECKOUT.lite
      && !paymentOpen && !suspendedOpen && !appointmentOpen && !packageOpen && !redeemOpen
      && !discountOpen && !noteOpen && !customerHistoryOpen
      && !stylistModalOpen && !priceModalOpen && !addonModalOpen,
    products,
    onProductScanned: handleBarcodeScan,
    onNotice: setShortcutNotice,
  })

  const handleQuickKeySelect = useCallback(async (key) => {
    let updated = null

    if (key.item_type === 'product' && key.product) {
      updated = await addProduct(key.product)
    } else if (key.service) {
      updated = await addService(key.service)
    }

    if (!updated) return

    const parentLines = getParentSaleLines(updated)
    setSelectedLineId(parentLines[parentLines.length - 1]?.id ?? null)
  }, [addProduct, addService])

  const handleAssignStylist = useCallback((lineId, stylistId, splitPercent) => {
    setSelectedLineId(lineId)
    assignStylist(lineId, stylistId, splitPercent)
  }, [assignStylist])

  const handleChangeLineQuantity = useCallback(async (lineId, quantity) => {
    setSelectedLineId(lineId)
    await changeLineQuantity(lineId, quantity)
  }, [changeLineQuantity])

  const handleAttachAddon = useCallback((parentLineId, addonServiceId) => {
    attachAddon(parentLineId, addonServiceId)
  }, [attachAddon])

  const handleDetachAddon = useCallback((addonLineId) => {
    detachAddon(addonLineId)
  }, [detachAddon])

  const handleRemoveLine = useCallback(async (lineId) => {
    const updated = await voidLine(lineId)
    if (updated) {
      if (selectedLineId === lineId) {
        handleSelectLine(null)
      }
    }
  }, [handleSelectLine, selectedLineId, voidLine])

  const handleLoadAddonOptions = useCallback((serviceId) => {
    ensureAddonOptions(serviceId)
  }, [ensureAddonOptions])

  const ensureGuestBeforeCheckout = useCallback((action) => {
    if (!SALON_DESK_CHECKOUT.requireGuestForCheckout || hasAssignedCustomer(sale)) {
      action(sale)
      return
    }

    if (!canPaySaleLines(sale) && !canHoldSaleLines(sale)) {
      return
    }

    pendingGuestActionRef.current = action
    setRequiredGuestOpen(true)
  }, [sale])

  const handleOpenPayment = useCallback(() => {
    ensureGuestBeforeCheckout((updatedSale) => {
      if (canOpenPaymentModal(updatedSale)) {
        setPaymentOpen(true)
      }
    })
  }, [ensureGuestBeforeCheckout])

  const handleRequiredGuestRegistered = useCallback(async (customer) => {
    const updated = await assignCustomer(customer.id)
    const nextGender = getSaleGenderPrice(updated)
    if (nextGender) {
      setCatalogGenderFilter(nextGender)
    }
    setRequiredGuestOpen(false)
    const pending = pendingGuestActionRef.current
    pendingGuestActionRef.current = null
    if (updated && pending) {
      await pending(updated)
    }
  }, [assignCustomer])

  const handleCloseRequiredGuest = useCallback(() => {
    if (mutating) return
    setRequiredGuestOpen(false)
    pendingGuestActionRef.current = null
  }, [mutating])

  const handleClosePayment = useCallback(async () => {
    setPaymentOpen(false)
    setReceipt(null)
    setTaxInvoice(null)
    setServiceSlips(null)
    if (sale?.status === 'completed') {
      await startNewTicket()
    }
  }, [sale?.status, startNewTicket])

  const handlePrintReceipt = useCallback(() => {
    printThermalReceipt(receiptRootRef.current)
  }, [])

  const loadLastCompletedSale = useCallback(async () => {
    if (!activeBranchId) {
      setLastCompletedSale(null)
      return null
    }

    const remembered = getRememberedLastCompletedSale(activeBranchId)
    if (remembered) {
      setLastCompletedSale(remembered)
    }

    try {
      const latest = await fetchLastCompletedSale()
      if (latest) {
        setLastCompletedSale(latest)
        rememberLastCompletedSale(activeBranchId, latest)
      } else if (!remembered) {
        setLastCompletedSale(null)
      }
      return latest ?? remembered
    } catch {
      return remembered
    }
  }, [activeBranchId])

  const handleReprintLastReceipt = useCallback(async () => {
    setReprintError('')
    setReprinting(true)

    try {
      let target = lastCompletedSale ?? getRememberedLastCompletedSale(activeBranchId)
      if (!target?.id) {
        target = await loadLastCompletedSale()
      }

      if (!target?.id) {
        setReprintError('No completed sale to reprint for this branch.')
        return
      }

      const receiptDoc = await fetchSaleReceipt(target.id)
      setReceipt(receiptDoc)
      window.setTimeout(() => {
        printThermalReceipt(receiptRootRef.current)
      }, 100)
    } catch {
      setReprintError('Unable to reprint the last receipt.')
    } finally {
      setReprinting(false)
    }
  }, [activeBranchId, lastCompletedSale, loadLastCompletedSale])

  const handlePrintTaxInvoice = useCallback(() => {
    printTaxInvoice(taxInvoiceRootRef.current)
  }, [])

  const loadAndPrintServiceSlips = useCallback(async (saleId) => {
    if (!saleId) return null

    try {
      const document = await fetchSaleServiceSlips(saleId)
      setServiceSlips(document)
      window.setTimeout(() => {
        printServiceSlips(serviceSlipRootRef.current)
      }, 100)
      return document
    } catch {
      setServiceSlips(null)
      return null
    }
  }, [])

  const handlePrintServiceSlips = useCallback(async () => {
    if (!sale?.id) return
    await loadAndPrintServiceSlips(sale.id)
  }, [loadAndPrintServiceSlips, sale?.id])

  const handleOpenSuspended = useCallback(async () => {
    setAppointmentOpen(false)
    setPackageOpen(false)
    setRedeemOpen(false)
    setDiscountOpen(false)
    setNoteOpen(false)
    await loadSuspendedSales()
    setSuspendedOpen(true)
  }, [loadSuspendedSales])

  const handleCloseSuspended = useCallback(() => {
    setSuspendedOpen(false)
  }, [])

  const handleHoldSale = useCallback(async () => {
    ensureGuestBeforeCheckout(async (updatedSale) => {
      if (!canHoldSaleLines(updatedSale)) return
      const draft = await holdCurrentSale(updatedSale)
      if (draft) {
        const nextGender = getSaleGenderPrice(draft)
        if (nextGender) {
          setCatalogGenderFilter(nextGender)
        }
      }
    })
  }, [ensureGuestBeforeCheckout, holdCurrentSale])

  const handleRecallSale = useCallback(async (saleId) => {
    const recalled = await recallSuspendedSale(saleId)
    if (!recalled) return false

    const nextGender = getSaleGenderPrice(recalled)
    if (nextGender) {
      setCatalogGenderFilter(nextGender)
    }
    setAppointmentOpen(false)
    return true
  }, [recallSuspendedSale])

  const handleAppointmentShortcut = useCallback(() => {
    setSuspendedOpen(false)
    setAppointmentOpen(true)
  }, [])

  const handleCloseAppointment = useCallback(() => {
    if (!mutating) {
      setAppointmentOpen(false)
    }
  }, [mutating])

  const handlePackageShortcut = useCallback(() => {
    setPackageOpen(true)
  }, [])

  const handleClosePackage = useCallback(() => {
    if (!mutating) {
      setPackageOpen(false)
    }
  }, [mutating])

  const handleRedeemShortcut = useCallback(() => {
    setRedeemOpen(true)
  }, [])

  const handleCloseRedeem = useCallback(() => {
    if (!mutating) {
      setRedeemOpen(false)
    }
  }, [mutating])

  const handleOpenDiscount = useCallback(() => {
    setDiscountTab(selectedLineId ? 'line' : 'bill')
    setDiscountOpen(true)
  }, [selectedLineId])

  const handleOpenVoucher = useCallback(() => {
    setDiscountTab('voucher')
    setDiscountOpen(true)
  }, [])

  const handleCloseDiscount = useCallback(() => {
    if (!mutating) setDiscountOpen(false)
  }, [mutating])

  const handleOpenNote = useCallback(() => {
    setNoteOpen(true)
  }, [])

  const handleCloseNote = useCallback(() => {
    if (!mutating) setNoteOpen(false)
  }, [mutating])

  const handleOpenBillDiscount = useCallback(() => {
    setDiscountTab('bill')
    setDiscountOpen(true)
  }, [])

  const handleOpenLineDiscount = useCallback(() => {
    setDiscountTab('line')
    setDiscountOpen(true)
  }, [])

  const selectedLine = useMemo(() => {
    if (!sale || selectedLineId == null) return null
    return getParentSaleLinesForDisplay(sale).find((line) => line.id == selectedLineId) ?? null
  }, [sale, selectedLineId])

  const selectedAddonOptions = useMemo(() => {
    if (!selectedLine?.service_id) return []
    return addonOptionsByService[selectedLine.service_id] ?? []
  }, [addonOptionsByService, selectedLine?.service_id])

  useEffect(() => {
    if (selectedLine?.service_id) {
      ensureAddonOptions(selectedLine.service_id)
    }
  }, [ensureAddonOptions, selectedLine?.service_id])

  const handleDuplicateLine = useCallback(async () => {
    const line = getParentSaleLinesForDisplay(sale).find((item) => item.id === selectedLineId)
    if (!line) return
    const updated = await duplicateLine(line)
    if (updated) {
      const parentLines = getParentSaleLines(updated)
      const lineId = parentLines[parentLines.length - 1]?.id ?? null
      setSelectedLineId(lineId)
    }
  }, [duplicateLine, sale, selectedLineId])

  const handleSendToFloor = useCallback(async () => {
    const ok = await enqueueToFloorQueue()
    if (ok) {
      setShortcutNotice('Guest added to floor queue.')
      window.setTimeout(() => setShortcutNotice(''), 4000)
    }
  }, [enqueueToFloorQueue])

  const handleOperation = useCallback((actionId) => {
    switch (actionId) {
      case 'stylist':
        if (!selectedLineId) return
        setStylistFocusSplit(false)
        setStylistModalOpen(true)
        return
      case 'split':
        if (!selectedLineId) return
        setStylistFocusSplit(true)
        setStylistModalOpen(true)
        return
      case 'qtyPlus':
        if (!selectedLine) return
        handleChangeLineQuantity(selectedLine.id, adjustLineQuantity(selectedLine.quantity, 1))
        return
      case 'qtyMinus':
        if (!selectedLine) return
        handleChangeLineQuantity(selectedLine.id, adjustLineQuantity(selectedLine.quantity, -1))
        return
      case 'lineDiscount':
        handleOpenLineDiscount()
        return
      case 'price':
        if (!selectedLineId) return
        setPriceModalOpen(true)
        return
      case 'duplicate':
        handleDuplicateLine()
        return
      case 'addon':
        if (!selectedLineId) return
        setAddonModalOpen(true)
        return
      case 'delete':
        if (!selectedLineId) return
        handleRemoveLine(selectedLineId)
        return
      case 'package':
        handlePackageShortcut()
        return
      case 'redeem':
        handleRedeemShortcut()
        return
      case 'floorQueue':
        handleSendToFloor()
        return
      case 'lineStatus':
        if (!selectedLineId) return
        advanceLineStatus(selectedLineId)
        return
      case 'hold':
        handleHoldSale()
        return
      case 'recall':
        handleOpenSuspended()
        return
      case 'voucher':
        handleOpenVoucher()
        return
      case 'ticketNote':
        handleOpenNote()
        return
      case 'billDiscount':
        handleOpenBillDiscount()
        return
      default:
    }
  }, [
    handleChangeLineQuantity,
    handleDuplicateLine,
    handleSendToFloor,
    advanceLineStatus,
    handleHoldSale,
    handleOpenBillDiscount,
    handleOpenLineDiscount,
    handleOpenNote,
    handleOpenSuspended,
    handleOpenVoucher,
    handlePackageShortcut,
    handleRedeemShortcut,
    handleRemoveLine,
    selectedLine,
    selectedLineId,
  ])

  const handleUpdateLineUnitPrice = useCallback(async (lineId, unitPrice) => {
    await updateLineDetails(lineId, { unit_price: unitPrice })
  }, [updateLineDetails])

  const handleClearLineSelection = useCallback(() => {
    setSelectedLineId(null)
  }, [])

  const handleDeleteLine = useCallback(async () => {
    if (!selectedLineId) return
    await handleRemoveLine(selectedLineId)
  }, [handleRemoveLine, selectedLineId])

  const handleComplimentary = useCallback(() => {
    setDiscountTab('line')
    setDiscountOpen(true)
  }, [])

  const handleSaveTicketNote = useCallback(async (notes) => {
    await updateSaleNotes(notes || null)
  }, [updateSaleNotes])

  const handleOpenCustomerHistory = useCallback(() => {
    const customer = getAssignedCustomer(sale)
    if (!customer?.id) return
    setCustomerHistoryOpen(true)
  }, [sale])

  const handleCloseCustomerHistory = useCallback(() => {
    setCustomerHistoryOpen(false)
  }, [])

  const historyCustomer = getAssignedCustomer(sale)

  const handleNewCustomer = useCallback(() => {
    focusPosCustomerSearch()
  }, [])

  const handleSellPackage = useCallback(async (payload) => {
    const updated = await sellPackage(payload)
    if (!updated) return false

    const parentLines = getParentSaleLines(updated)
    setSelectedLineId(parentLines[parentLines.length - 1]?.id ?? null)
    setShortcutNotice('Package added to ticket.')
    return true
  }, [sellPackage])

  const handleRedeemPackage = useCallback(async (payload) => {
    const updated = await redeemPackageService(payload)
    if (!updated) return false

    const parentLines = getParentSaleLines(updated)
    setSelectedLineId(parentLines[parentLines.length - 1]?.id ?? null)
    setShortcutNotice('Covered service added to ticket.')
    return true
  }, [redeemPackageService])

  const handleRedeemPackageBatch = useCallback(async (payload) => {
    const updated = await redeemPackageServices(payload)
    if (!updated) return false

    const parentLines = getParentSaleLines(updated)
    setSelectedLineId(parentLines[parentLines.length - 1]?.id ?? null)
    const count = payload.service_ids?.length ?? 0
    setShortcutNotice(
      count > 1
        ? `${count} covered services added to ticket.`
        : 'Covered service added to ticket.',
    )
    return true
  }, [redeemPackageServices])

  const handleLoadAppointment = useCallback(async (appointmentId) => {
    const loaded = await loadAppointmentOntoTicket(appointmentId)
    if (!loaded) return false

    const nextGender = getSaleGenderPrice(loaded)
    if (nextGender) {
      setCatalogGenderFilter(nextGender)
    }

    setShortcutNotice('Appointment loaded onto ticket.')
    return true
  }, [loadAppointmentOntoTicket])

  useEffect(() => {
    if (!shortcutNotice) return undefined

    const timer = window.setTimeout(() => {
      setShortcutNotice('')
    }, 3000)

    return () => window.clearTimeout(timer)
  }, [shortcutNotice])

  useEffect(() => {
    const parentLines = getParentSaleLines(sale)
    if (!parentLines.length) {
      setSelectedLineId(null)
      return
    }

    if (selectedLineId && parentLines.some((line) => line.id === selectedLineId)) {
      return
    }

    setSelectedLineId(parentLines[parentLines.length - 1]?.id ?? null)
  }, [sale, selectedLineId])

  usePosKeyboard({
    sale,
    mutating,
    paymentOpen,
    suspendedOpen,
    appointmentOpen,
    packageOpen,
    redeemOpen,
    discountOpen,
    noteOpen,
    customerHistoryOpen,
    selectedLineId,
    onFocusCustomer: focusPosCustomerSearch,
    onOpenPayment: handleOpenPayment,
    onHoldSale: handleHoldSale,
    onOpenSuspended: handleOpenSuspended,
    onOpenDiscount: handleOpenDiscount,
    onOpenVoucher: handleOpenVoucher,
    onClosePayment: handleClosePayment,
    onCloseSuspended: handleCloseSuspended,
    onCloseAppointment: handleCloseAppointment,
    onClosePackage: handleClosePackage,
    onCloseRedeem: handleCloseRedeem,
    onCloseDiscount: handleCloseDiscount,
    onCloseNote: handleCloseNote,
    onCloseCustomerHistory: handleCloseCustomerHistory,
    onClearLineSelection: handleClearLineSelection,
    onAppointmentShortcut: handleAppointmentShortcut,
    onPackageShortcut: handlePackageShortcut,
    onRedeemShortcut: handleRedeemShortcut,
    onChangeLineQuantity: handleChangeLineQuantity,
  })

  const handlePaySubmit = useCallback(async (payload) => {
    const result = await paySale(payload)
    if (!result) return null

    let receiptDoc = null
    let taxInvoiceDoc = null
    let serviceSlipDoc = null
    try {
      ;[receiptDoc, taxInvoiceDoc, serviceSlipDoc] = await Promise.all([
        fetchSaleReceipt(result.sale.id),
        fetchSaleTaxInvoice(result.sale.id),
        fetchSaleServiceSlips(result.sale.id),
      ])
      setReceipt(receiptDoc)
      setTaxInvoice(taxInvoiceDoc)
      setServiceSlips(serviceSlipDoc)
    } catch {
      setReceipt(null)
      setTaxInvoice(null)
      setServiceSlips(null)
    }

    const completedSummary = {
      id: result.sale.id,
      sale_number: result.sale.sale_number,
      status: result.sale.status,
      completed_at: result.sale.completed_at ?? receiptDoc?.sale?.completed_at ?? null,
      total: result.sale.total,
    }
    rememberLastCompletedSale(activeBranchId, completedSummary)
    setLastCompletedSale(completedSummary)

    return {
      changeDue: result.changeDue,
      receipt: receiptDoc,
      taxInvoice: taxInvoiceDoc,
      serviceSlips: serviceSlipDoc,
    }
  }, [activeBranchId, paySale])

  useEffect(() => {
    if (!paymentOpen || SALON_DESK_CHECKOUT.lite) return undefined

    let active = true
    fetchPaymentGatewaySettings()
      .then((settings) => {
        if (active) {
          setGatewaySettings({
            enabled: Boolean(settings?.enabled) && settings?.provider !== 'none',
            test_mode: settings?.test_mode !== false,
          })
        }
      })
      .catch(() => {
        if (active) {
          setGatewaySettings({ enabled: false, test_mode: true })
        }
      })

    return () => {
      active = false
    }
  }, [paymentOpen, activeBranchId])

  const handleChargeGateway = useCallback(async (payload) => {
    if (!sale?.id) return null
    try {
      const data = await chargeSaleGateway(sale.id, payload)
      return data.transaction ?? null
    } catch (err) {
      throw new Error(apiErrorMessage(err, 'Gateway capture failed.'))
    }
  }, [sale?.id])

  useEffect(() => {
    loadLastCompletedSale()
  }, [loadLastCompletedSale])

  useEffect(() => {
    if (!activeBranchId) {
      return
    }

    loadSuspendedSales()
  }, [activeBranchId, loadSuspendedSales])

  useEffect(() => {
    if (!receipt || !paymentOpen) return undefined

    const timer = window.setTimeout(() => {
      printThermalReceipt(receiptRootRef.current)
    }, 150)

    return () => window.clearTimeout(timer)
  }, [paymentOpen, receipt])

  const saleMeta = toSaleHeaderMeta(sale, branchLabel)
  const totals = toPosTotals(sale)
  const canSendToFloor = Boolean(
    getAssignedCustomer(sale)
    && getParentSaleLines(sale).some((line) => line.line_type === 'service'),
  )

  const packageBalanceRefreshToken = useMemo(
    () => `${sale?.customer_id ?? ''}-${sale?.lines?.length ?? 0}-${sale?.updated_at ?? ''}`,
    [sale?.customer_id, sale?.lines?.length, sale?.updated_at],
  )

  return (
    <>
      {dayGateLoading ? (
        <div className="flex min-h-[50vh] items-center justify-center text-muted-foreground">
          Checking cash day…
        </div>
      ) : null}
      {!dayGateLoading && !dayOpen ? (
        <DayOpenGate busy={dayGateBusy} error={dayGateError} onOpenDay={handleDayOpenFromGate} />
      ) : null}
      {!dayGateLoading && dayOpen ? (
        <>
      <PosOfflineBanner />
      <div ref={receiptRootRef}>
        <PosThermalReceipt receipt={receipt} />
      </div>
      <div ref={taxInvoiceRootRef}>
        <PosTaxInvoice invoice={taxInvoice} />
      </div>
      <div ref={serviceSlipRootRef}>
        <PosServiceSlips document={serviceSlips} />
      </div>

      <PosLayout
        catalogMode={catalogMode}
        onCatalogModeChange={setCatalogMode}
        activeCategoryId={activeCategoryId}
        onCategoryChange={setActiveCategoryId}
        catalogGenderFilter={catalogGenderFilter}
        pricingGender={pricingGender}
        onGenderChange={handleGenderChange}
        categories={categories}
        services={services}
        loadingCategories={loadingCategories}
        loadingServices={loadingServices}
        catalogError={catalogError}
        products={products}
        loadingProducts={loadingProducts}
        productsError={productsError}
        onServiceSelect={handleServiceSelect}
        onProductSelect={handleProductSelect}
        onQuickKeySelect={handleQuickKeySelect}
        quickKeys={quickKeys}
        loadingQuickKeys={loadingQuickKeys}
        quickKeysError={quickKeysError}
        sale={sale}
        saleMeta={saleMeta}
        stylists={stylists}
        addonOptionsByService={addonOptionsByService}
        saleLoading={saleLoading}
        saleMutating={mutating}
        saleError={saleError}
        customerError={customerError}
        holdError={holdError}
        floorQueueError={floorQueueError}
        canSendToFloor={SALON_DESK_CHECKOUT.showFloorQueue && canSendToFloor}
        onSendToFloor={SALON_DESK_CHECKOUT.showFloorQueue ? handleSendToFloor : undefined}
        onHoldTicket={handleHoldSale}
        onSelectCustomer={handleSelectCustomer}
        onClearCustomer={handleClearCustomer}
        onOpenCustomerHistory={handleOpenCustomerHistory}
        onRedeemLoyalty={applyLoyaltyRedemptionToSale}
        onRemoveLoyaltyRedeem={removeLoyaltyRedemptionFromSale}
        loyaltyError={loyaltyError}
        onOpenPayment={handleOpenPayment}
        onOperation={handleOperation}
        onPrintServiceSlips={SALON_DESK_CHECKOUT.showServiceSlips ? handlePrintServiceSlips : undefined}
        selectedLineId={selectedLineId}
        onSelectLine={handleSelectLine}
        onRemoveLine={handleRemoveLine}
        shortcutNotice={shortcutNotice}
        packageBalanceRefreshToken={packageBalanceRefreshToken}
        totals={totals}
        heldTicketCount={suspendedSales.length}
        onLoadTicket={handleOpenSuspended}
        onLoadAppointment={handleAppointmentShortcut}
      />

      <PosSuspendedModal
        open={suspendedOpen}
        onClose={handleCloseSuspended}
        sales={suspendedSales}
        loading={loadingSuspended}
        submitting={mutating}
        onRecall={handleRecallSale}
        error={holdError}
      />

      <PosAppointmentModal
        open={appointmentOpen}
        onClose={handleCloseAppointment}
        submitting={mutating}
        onLoad={handleLoadAppointment}
        error={appointmentError}
      />

      <PosPackageWizardModal
        open={SALON_DESK_CHECKOUT.showPackageModals && packageOpen}
        sale={sale}
        onClose={handleClosePackage}
        submitting={mutating}
        onSell={handleSellPackage}
        error={packageError}
      />

      <PosPackageRedeemModal
        open={SALON_DESK_CHECKOUT.showPackageModals && redeemOpen}
        sale={sale}
        onClose={handleCloseRedeem}
        submitting={mutating}
        onRedeem={handleRedeemPackage}
        onRedeemBatch={handleRedeemPackageBatch}
        error={redeemError}
      />

      <PosPaymentModal
        open={paymentOpen}
        onClose={handleClosePayment}
        sale={sale}
        paymentMethods={paymentMethods}
        loadingMethods={loadingPaymentMethods}
        submitting={mutating}
        onSubmit={handlePaySubmit}
        onPrintReceipt={handlePrintReceipt}
        onPrintTaxInvoice={SALON_DESK_CHECKOUT.showTaxInvoice ? handlePrintTaxInvoice : undefined}
        onPrintServiceSlips={SALON_DESK_CHECKOUT.showServiceSlips ? handlePrintServiceSlips : undefined}
        apiError={paymentError}
        stylists={stylists}
        gatewayEnabled={!SALON_DESK_CHECKOUT.lite && gatewaySettings.enabled}
        gatewayTestMode={gatewaySettings.test_mode}
        onChargeGateway={SALON_DESK_CHECKOUT.showGateway ? handleChargeGateway : undefined}
        onApplyPromoCode={SALON_DESK_CHECKOUT.showPromoCodes ? applyPromoCodeToSale : undefined}
        onRemovePromoCode={SALON_DESK_CHECKOUT.showPromoCodes ? removePromoCodeFromSale : undefined}
      />

      <PosDiscountModal
        open={discountOpen}
        onClose={handleCloseDiscount}
        initialTab={discountTab}
        sale={sale}
        lineId={selectedLineId}
        submitting={mutating}
        apiError={discountError}
        onApplyLineDiscount={async ({ lineId, ...payload }) => {
          await applyLineDiscountToSale(lineId, payload)
        }}
        onApplyHeaderDiscount={async (payload) => {
          await applyHeaderDiscountToSale(payload)
        }}
        onApplyPromoCode={async (payload) => {
          await applyPromoCodeToSale(payload)
        }}
        onRemovePromoCode={removePromoCodeFromSale}
      />

      <PosTicketNoteModal
        open={noteOpen}
        onClose={handleCloseNote}
        initialNotes={sale?.notes ?? ''}
        submitting={mutating}
        onSave={handleSaveTicketNote}
      />

      <PosLineStylistModal
        open={stylistModalOpen}
        onClose={() => setStylistModalOpen(false)}
        line={selectedLine}
        stylists={stylists}
        disabled={mutating}
        focusSplit={stylistFocusSplit}
        onAssign={handleAssignStylist}
      />

      <PosLinePriceModal
        open={priceModalOpen}
        onClose={() => setPriceModalOpen(false)}
        line={selectedLine}
        disabled={mutating}
        onSave={handleUpdateLineUnitPrice}
      />

      {sale && selectedLine && SALON_DESK_CHECKOUT.showAddons ? (
        <PosLineAddonModal
          open={addonModalOpen}
          onClose={() => setAddonModalOpen(false)}
          sale={sale}
          line={selectedLine}
          addonOptions={selectedAddonOptions}
          disabled={mutating}
          onAttach={handleAttachAddon}
          onDetach={handleDetachAddon}
        />
      ) : null}

      <PosCustomerHistoryModal
        open={customerHistoryOpen}
        onClose={handleCloseCustomerHistory}
        customerId={historyCustomer?.id ?? null}
        customerName={historyCustomer?.name ?? ''}
        customerPhone={historyCustomer?.phone ?? null}
      />

      <PosNewGuestModal
        open={requiredGuestOpen}
        onClose={handleCloseRequiredGuest}
        onRegistered={handleRequiredGuestRegistered}
        phoneCountry={guestPhoneCountry}
        disabled={mutating}
        required
      />
        </>
      ) : null}
    </>
  )
}
